import { FC, useEffect, useMemo, useState } from 'react';
import Script from 'next/script';

import { clearGladlyUser, gladlyInit, setGladlyUser } from 'shared/utils/gladly/gladly';
import { useSessionValue } from 'appLayer/auth';
import { useStoreValue } from 'state/hooks/useStore';
import { MONITORING_TAGS } from '@portal/constants/monitoring';
import { datadogRum } from '@datadog/browser-rum-slim';

const isGladlyInitialized = () => !!window?.Gladly?.setUser;

const GladlyChat: FC = () => {
  const store = useStoreValue();
  const [init, setInit] = useState<boolean>(false);
  const session = useSessionValue();

  const gladlyAppId = useMemo((): string => {
    switch (store) {
      case 'eu':
        return 'athleticgreens.com';
      case 'uk':
        return 'athleticgreens.com-UK';
      case 'eud':
        return 'athleticgreens.com';
      default:
        return 'athleticgreens.com-US';
    }
  }, [store]);

  /**
   * Set user info is user signed up
   */
  useEffect(() => {
    if (isGladlyInitialized()) {
      return;
    }
    if (session?.customer) {
      const user = session?.customer;

      setGladlyUser({
        name: `${user?.firstName} ${user?.lastName}`,
        email: user.email || ''
      });
    } else {
      clearGladlyUser();
    }
  }, [session, init]);

  useEffect(() => {
    let count = 0;

    if (!gladlyAppId || typeof window === 'undefined') return;

    const checkIfFunctionExists = () => {
      if (isGladlyInitialized()) return;

      if (typeof window.Gladly?.init !== 'undefined' && count <= 20) {
        gladlyInit(gladlyAppId)
          .then((result) => {
            setInit(result);
          })
          .catch(() => {
            setTimeout(checkIfFunctionExists, 200);
            count += 1;
          });
        /**
         * Try 4 seconds to init gladly chat
         */
      } else if (count <= 20) {
        setTimeout(checkIfFunctionExists, 200);
        count += 1;
      }
    };

    checkIfFunctionExists();
  }, [gladlyAppId]);

  return (
    /**
     * Gladly implement its own script after initialize and
     * onLoad now working here after reload page
     */
    <Script
      id="gladlyWidget"
      strategy="afterInteractive"
      crossOrigin="anonymous"
      onError={(e) => {
        datadogRum.addError(new Error('gladly_widget_script_load_error'), {
          tags: { [MONITORING_TAGS.GLADLY_CHAT]: true },
          store,
          gladlyAppId,
          event: String(e)
        });
      }}
      dangerouslySetInnerHTML={{
        __html: `!function(c,n,r,t){if(!c[r]){var i,d,p=[];d="PROD"!==t&&t?"STAGING"===t?"https://cdn.gladly.qa/gladly/chat-sdk/widget.js":t:"https://cdn.gladly.com/chat-sdk/widget.js",c[r]={init:function(){i=arguments;var e={then:function(t){return p.push({type:"t",next:t}),e},catch:function(t){return p.push({type:"c",next:t}),e}};return e}},c.__onHelpAppHostReady__=function(t){if(delete c.__onHelpAppHostReady__,(c[r]=t).loaderCdn=d,i)for(var e=t.init.apply(t,i),n=0;n<p.length;n++){var a=p[n];e="t"===a.type?e.then(a.next):e.catch(a.next)}},function(){try{var t=n.getElementsByTagName("script")[0],e=n.createElement("script");e.async=!0,e.src=d+"?q="+(new Date).getTime(),t.parentNode.insertBefore(e,t)}catch(t){}}()}}
      (window,document,'Gladly','PROD')`
      }}
    />
  );
};

export default GladlyChat;
